import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Icon } from '@iconify/react';
import mabarService, { EventDetailResponse } from '@/services/mabarService';
import { MabarParticipantsSkeleton } from '@/components/client/skeleton/ClientSkeletons';
import showToast from '@/components/ui/Toast';

const AVATAR_COLORS = [
  '#FC480D', // Orange
  '#F97316', // Light orange
  '#3B82F6', // Blue
  '#8B5CF6', // Purple
  '#10B981', // Emerald
  '#D97706', // Amber
  '#06B6D4', // Cyan
  '#EC4899', // Pink
  '#6366F1', // Indigo
  '#84CC16', // Lime
];

const getAvatarColor = (name: string) => {
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = name.charCodeAt(i) + ((hash << 5) - hash);
  }
  const index = Math.abs(hash) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

const getInitials = (name: string) => {
  if (!name) return 'S';
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

const isInvalidOrZeroDate = (str?: string): boolean => {
  if (!str) return true;
  const trimmed = str.trim();
  return (
    trimmed.startsWith('0001-01-01') ||
    trimmed.startsWith('1970-01-01') ||
    trimmed.includes('0001-01-01T00:00:00')
  );
};

const formatJoinDate = (dateStr?: string, eventDateStr?: string) => {
  const validDateStr = !isInvalidOrZeroDate(dateStr)
    ? dateStr
    : !isInvalidOrZeroDate(eventDateStr)
    ? eventDateStr
    : undefined;
  if (!validDateStr) return '';

  let normalizedStr = validDateStr.trim();
  // Normalize string format e.g. "2026-08-14 14:37:21.677015+00" -> "2026-08-14T14:37:21.677015+00"
  if (/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}/.test(normalizedStr)) {
    normalizedStr = normalizedStr.replace(' ', 'T');
  }

  const d = new Date(normalizedStr);
  if (isNaN(d.getTime())) return '';

  const day = d.getDate();
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'Mei', 'Jun', 'Jul', 'Agu', 'Sep', 'Okt', 'Nov', 'Des'];
  const month = months[d.getMonth()];
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (dateStr && !isInvalidOrZeroDate(dateStr)) {
    return `${day} ${month}, ${hh}.${mm}`;
  }
  return `${day} ${month}`;
};

export const ClientMabarParticipantsPage: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [event, setEvent] = useState<EventDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'registered' | 'waiting_list'>('registered');

  useEffect(() => {
    const fetchEvent = async () => {
      if (!slug) return;
      setLoading(true);
      setError(null);
      try {
        const data = await mabarService.getEventBySlug(slug);
        if (!data) {
          setError('Event mabar tidak ditemukan');
        } else {
          setEvent(data);
        }
      } catch (err: any) {
        setError(err.message || 'Gagal memuat data event');
      } finally {
        setLoading(false);
      }
    };
    fetchEvent();
  }, [slug]);

  if (loading) {
    return <MabarParticipantsSkeleton />;
  }

  if (error || !event) {
    return (
      <div className="bg-[#F8FAFC] min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl p-6 text-center max-w-sm w-full border border-gray-100 shadow-xs space-y-3">
          <Icon icon="ph:warning-circle-bold" className="w-10 h-10 text-red-500 mx-auto" />
          <p className="text-sm font-bold text-[#0E1726]">{error || 'Event tidak ditemukan'}</p>
          <button
            onClick={() => navigate(-1)}
            className="text-xs font-bold text-[#FC480D] hover:underline"
          >
            Kembali
          </button>
        </div>
      </div>
    );
  }

  const allParticipants = event.participants || [];
  const registeredParticipants = allParticipants.filter(
    (p) => p.status !== 'waiting_list' && p.status !== 'canceled' && p.status !== 'unpaid'
  );
  const waitingListParticipants = allParticipants.filter(
    (p) => p.status === 'waiting_list'
  );

  const totalSlots = event.total_slots || 0;
  const filledSlots = registeredParticipants.length;
  const availableSlots = Math.max(0, event.available_slots ?? (totalSlots - filledSlots));
  const maxWaitingList = event.config?.waiting_list_slots || 6;
  const hostName = event.host?.name?.trim().toLowerCase();

  return (
    <div className="bg-[#F8FAFC] min-h-screen pb-12 pt-4 px-4 max-w-md mx-auto space-y-5 animate-fade-in">
      {/* TOP STATS CARD */}
      {activeTab === 'registered' ? (
        <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs grid grid-cols-3 divide-x divide-gray-100 text-center">
          <div className="px-2">
            <span className="text-xl font-extrabold text-[#FC480D] block leading-none">
              {filledSlots}
            </span>
            <span className="text-xs font-semibold text-gray-400 mt-1.5 block">Bergabung</span>
          </div>
          <div className="px-2">
            <span className="text-xl font-extrabold text-[#0E1726] block leading-none">
              {availableSlots}
            </span>
            <span className="text-xs font-semibold text-gray-400 mt-1.5 block">Slot kosong</span>
          </div>
          <div className="px-2">
            <span className="text-xl font-extrabold text-[#0E1726] block leading-none">
              {totalSlots}
            </span>
            <span className="text-xs font-semibold text-gray-400 mt-1.5 block">Kuota</span>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-3xl p-4 sm:p-5 border border-gray-100 shadow-2xs grid grid-cols-3 divide-x divide-gray-100 text-center">
          <div className="px-2">
            <span className="text-xl font-extrabold text-amber-600 block leading-none">
              {waitingListParticipants.length}
            </span>
            <span className="text-xs font-semibold text-gray-400 mt-1.5 block">Dalam antrean</span>
          </div>
          <div className="px-2">
            <span className="text-xl font-extrabold text-[#0E1726] block leading-none">
              {Math.max(0, maxWaitingList - waitingListParticipants.length)}
            </span>
            <span className="text-xs font-semibold text-gray-400 mt-1.5 block">Sisa antrean</span>
          </div>
          <div className="px-2">
            <span className="text-xl font-extrabold text-[#0E1726] block leading-none">
              {maxWaitingList}
            </span>
            <span className="text-xs font-semibold text-gray-400 mt-1.5 block">Batas antrean</span>
          </div>
        </div>
      )}

      {/* SEGMENTED TAB BUTTONS */}
      <div className="flex bg-gray-200/80 p-1 rounded-2xl gap-1">
        <button
          type="button"
          onClick={() => setActiveTab('registered')}
          className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
            activeTab === 'registered'
              ? 'bg-white text-[#0E1726] shadow-xs'
              : 'text-gray-500 hover:text-gray-800'
          }`}
        >
          <span>Pemain Terdaftar</span>
          <span
            className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
              activeTab === 'registered'
                ? 'bg-orange-100 text-[#FC480D]'
                : 'bg-gray-100 text-gray-500'
            }`}
          >
            {registeredParticipants.length}
          </span>
        </button>

        {(event.config?.waiting_list || waitingListParticipants.length > 0) && (
          <button
            type="button"
            onClick={() => setActiveTab('waiting_list')}
            className={`flex-1 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-1.5 cursor-pointer ${
              activeTab === 'waiting_list'
                ? 'bg-white text-amber-800 shadow-xs'
                : 'text-gray-500 hover:text-gray-800'
            }`}
          >
            <span>Waiting List</span>
            <span
              className={`px-1.5 py-0.5 rounded-full text-[10px] font-extrabold ${
                activeTab === 'waiting_list'
                  ? 'bg-amber-100 text-amber-800'
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              {waitingListParticipants.length}
            </span>
          </button>
        )}
      </div>

      {/* TAB 1: PEMAIN TERDAFTAR */}
      {activeTab === 'registered' && (
        <div className="space-y-4">
          <div className="space-y-2.5">
            <h2 className="text-base font-extrabold text-[#0E1726] px-1">Sudah bergabung</h2>

            {registeredParticipants.length === 0 ? (
              <div className="bg-white rounded-3xl p-6 border border-gray-100 text-center text-xs font-semibold text-gray-400 shadow-2xs">
                Belum ada pemain yang bergabung
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-2xs divide-y divide-gray-100 overflow-hidden">
                {registeredParticipants.map((p, idx) => {
                  const isHost =
                    (p as any).is_host ||
                    (hostName && p.name?.trim().toLowerCase() === hostName);

                  const roleNormalized = (p.role || '').toLowerCase();
                  const isKeeper =
                    roleNormalized.includes('kiper') ||
                    roleNormalized.includes('keeper') ||
                    roleNormalized.includes('gk');

                  const joinedAtRaw =
                    (p as any).joined_at || (p as any).created_at || (p as any).confirmed_at;
                  const joinedDateFormatted = formatJoinDate(joinedAtRaw, event.event_date);
                  const playerUsername = (p as any).username || '';
                  const playerEmail = (p as any).email || '';
                  const isDummy =
                    playerEmail.toLowerCase().includes('@mabar-sporto.id') ||
                    playerUsername.toLowerCase().includes('mabar-sporto') ||
                    Boolean((p as any).is_dummy);
                  const isClickable = Boolean(playerUsername && !isDummy);

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (isClickable) {
                          navigate(`/profile/${playerUsername}`);
                        } else {
                          showToast.error('Player tidak memiliki profil');
                        }
                      }}
                      className="p-3.5 px-4 flex items-center justify-between gap-3 transition-colors hover:bg-gray-50/70 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="w-5 text-xs font-bold text-gray-400 text-right shrink-0">
                          {idx + 1}.
                        </span>

                        {p.avatar ? (
                          <img
                            src={p.avatar}
                            alt={p.name}
                            className="w-10 h-10 rounded-full object-cover shrink-0 border border-gray-100"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-xs shrink-0"
                            style={{ backgroundColor: getAvatarColor(p.name || 'P') }}
                          >
                            {getInitials(p.name)}
                          </div>
                        )}

                        <div className="min-w-0 flex-1 space-y-0.5">
                          <div className="flex items-center gap-1.5 min-w-0">
                            <span className="text-sm font-bold text-[#0E1726] truncate">
                              {p.name}
                            </span>
                            {isHost && (
                              <span className="bg-orange-50 text-[#FC480D] border border-orange-100/60 text-[10px] font-extrabold px-1.5 py-0.2 rounded-md shrink-0">
                                Host
                              </span>
                            )}
                          </div>
                          {joinedDateFormatted && (
                            <p className="text-[11px] font-medium text-gray-400 truncate">
                              Gabung {joinedDateFormatted}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Role Badge */}
                      <div className="shrink-0">
                        {isKeeper ? (
                          <span className="bg-blue-50 border border-blue-100 text-blue-600 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                            <Icon icon="ph:hand-bold" className="w-3.5 h-3.5 text-blue-600" />
                            <span>{p.role || 'Kiper'}</span>
                          </span>
                        ) : (
                          <span className="bg-orange-50 border border-orange-100 text-[#FC480D] font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                            <Icon icon="ph:footprints-bold" className="w-3.5 h-3.5 text-[#FC480D]" />
                            <span>{p.role || 'Pemain'}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* SECTION 2: SLOT TERSISA */}
          {availableSlots > 0 && (
            <div className="space-y-2.5">
              <h2 className="text-base font-extrabold text-[#0E1726] px-1">Slot tersisa</h2>

              <div className="bg-white rounded-3xl border border-gray-100 shadow-2xs divide-y divide-gray-100 overflow-hidden">
                {Array.from({ length: availableSlots }).map((_, idx) => (
                  <div
                    key={idx}
                    className="p-3.5 px-4 flex items-center gap-3 text-gray-400"
                  >
                    <span className="w-5 text-xs font-bold text-gray-400 text-right shrink-0">
                      {registeredParticipants.length + idx + 1}.
                    </span>

                    <div className="w-10 h-10 rounded-full bg-gray-100/70 border border-gray-200/50 flex items-center justify-center text-gray-300 shrink-0">
                      <Icon icon="ph:user-bold" className="w-5 h-5 text-gray-300" />
                    </div>

                    <span className="text-xs font-semibold text-gray-400">
                      Menunggu pemain
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Link to Waiting List */}
          {waitingListParticipants.length > 0 && (
            <div className="text-center pt-1">
              <button
                type="button"
                onClick={() => setActiveTab('waiting_list')}
                className="text-xs font-bold text-amber-700 hover:text-amber-800 hover:underline inline-flex items-center gap-1.5 cursor-pointer py-1"
              >
                <Icon icon="ph:users-three-bold" className="w-4 h-4 text-amber-600" />
                <span>Lihat {waitingListParticipants.length} pemain di daftar tunggu (Waiting List) →</span>
              </button>
            </div>
          )}
        </div>
      )}

      {/* TAB 2: WAITING LIST */}
      {activeTab === 'waiting_list' && (
        <div className="space-y-4">
          {/* Informational banner */}
          <div className="p-3.5 bg-amber-50/90 border border-amber-200/80 rounded-2xl flex items-start gap-2.5 text-xs text-amber-900 shadow-2xs">
            <Icon icon="ph:info-bold" className="w-4 h-4 text-amber-600 mt-0.5 shrink-0" />
            <p className="leading-relaxed">
              Urutan daftar tunggu berdasarkan waktu pendaftaran (FIFO). Jika peserta aktif unlist atau dibatalkan host, pemain di urutan pertama otomatis dipromosikan ke event.
            </p>
          </div>

          <div className="space-y-2.5">
            <h2 className="text-base font-extrabold text-[#0E1726] px-1">
              Antrean Waiting List ({waitingListParticipants.length})
            </h2>

            {waitingListParticipants.length === 0 ? (
              <div className="bg-white rounded-3xl p-6 border border-gray-100 text-center text-xs font-semibold text-gray-400 shadow-2xs">
                Belum ada pemain di antrean waiting list
              </div>
            ) : (
              <div className="bg-white rounded-3xl border border-gray-100 shadow-2xs divide-y divide-gray-100 overflow-hidden">
                {waitingListParticipants.map((p, idx) => {
                  const joinedAtRaw =
                    (p as any).joined_at || (p as any).created_at;
                  const joinedDateFormatted = formatJoinDate(joinedAtRaw, event.event_date);
                  const playerUsername = (p as any).username || '';
                  const playerEmail = (p as any).email || '';
                  const isDummy =
                    playerEmail.toLowerCase().includes('@mabar-sporto.id') ||
                    playerUsername.toLowerCase().includes('mabar-sporto') ||
                    Boolean((p as any).is_dummy);
                  const isClickable = Boolean(playerUsername && !isDummy);

                  return (
                    <div
                      key={idx}
                      onClick={() => {
                        if (isClickable) {
                          navigate(`/profile/${playerUsername}`);
                        } else {
                          showToast.error('Player tidak memiliki profil');
                        }
                      }}
                      className="p-3.5 px-4 flex items-center justify-between gap-3 transition-colors hover:bg-gray-50/70 cursor-pointer"
                    >
                      <div className="flex items-center gap-3 min-w-0 flex-1">
                        <span className="w-7 text-xs font-extrabold text-amber-700 bg-amber-50 border border-amber-200/80 rounded-lg py-1 text-center shrink-0">
                          #{idx + 1}
                        </span>

                        {p.avatar ? (
                          <img
                            src={p.avatar}
                            alt={p.name}
                            className="w-10 h-10 rounded-full object-cover shrink-0 border border-gray-100"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-white text-xs shrink-0"
                            style={{ backgroundColor: getAvatarColor(p.name || 'P') }}
                          >
                            {getInitials(p.name)}
                          </div>
                        )}

                        <div className="min-w-0 flex-1 space-y-0.5">
                          <span className="text-sm font-bold text-[#0E1726] truncate block">
                            {p.name}
                          </span>
                          {joinedDateFormatted && (
                            <p className="text-[11px] font-medium text-gray-400 truncate">
                              Daftar {joinedDateFormatted}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Waiting List Role Badge */}
                      <div className="shrink-0">
                        <span className="bg-amber-50 border border-amber-200 text-amber-800 font-bold text-xs px-3 py-1 rounded-full flex items-center gap-1.5">
                          <Icon icon="ph:hourglass-medium-bold" className="w-3.5 h-3.5 text-amber-600" />
                          <span>{p.role || 'Pemain'}</span>
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="text-center pt-1">
            <button
              type="button"
              onClick={() => setActiveTab('registered')}
              className="text-xs font-bold text-[#FC480D] hover:underline inline-flex items-center gap-1 cursor-pointer py-1"
            >
              <span>← Kembali ke Pemain Terdaftar ({registeredParticipants.length})</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientMabarParticipantsPage;
